import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Lock, Loader2, ShieldCheck } from "lucide-react";

export const AdminLogin = () => {
  const { login, isAuthed, checking } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (checking) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[var(--bw-secondary)]">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (isAuthed) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Enter username and password.");
      return;
    }
    setSubmitting(true);
    try {
      await login(username, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-[calc(100vh-4rem)] bg-[var(--bw-admin-bg)] flex items-center justify-center px-6"
    >
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white border border-[var(--bw-primary)]/20 p-8 md:p-10"
      >
        <div className="flex items-center gap-2 text-[var(--bw-accent)]">
          <ShieldCheck className="w-5 h-5" />
          <p className="font-mono-plex text-xs uppercase tracking-[0.35em]">
            admin access
          </p>
        </div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-3">
          Sign in.
        </h1>
        <p className="text-sm text-[var(--bw-secondary)] mt-3">
          Restricted area. Manage the shelf with your admin credentials.
        </p>

        <div className="mt-8 space-y-4">
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
              Username
            </label>
            <input
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm"
            />
          </div>
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
              Password
            </label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm"
            />
          </div>
        </div>

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={submitting}
          className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--bw-primary)] text-[var(--bw-bg)] py-3.5 hover:bg-[var(--bw-accent)] transition-colors disabled:opacity-70"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Signing in…
            </>
          ) : (
            <>
              <Lock className="w-4 h-4" />
              Sign in
            </>
          )}
        </button>
      </form>
    </main>
  );
};

export default AdminLogin;
